import { useState, useEffect, useRef, useMemo } from "react";
import useItemSearch from "../../hooks/searchHooks/itemSearch";
import useGuideSearch from "../../hooks/searchHooks/wikiSearch";
import usePageSearch from "../../hooks/searchHooks/pageSearch";
import { fetchItems } from "../../utils/fetchItemData";
import { useModalStack } from "../../utils/modalContext";
import { useNavigate, useLocation } from "react-router-dom";

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation(); // Add to state

  // Search hooks
  const { searchItems, loading: loadingItems } = useItemSearch();
  const { searchGuides, loading: loadingGuides } = useGuideSearch();
  const { searchPages, loading: loadingPages } = usePageSearch();
  const { pushModal } = useModalStack();
  const navigate = useNavigate();

  // Add to main component
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Debounce the query input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300); // 300ms delay
    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  // Updated search hooks usage
  // In your SearchBar component
  const allResults = useMemo(() => {
    if (!debouncedQuery) return { items: [], pages: [] };

    // Process items - preserve original structure
    const itemResults = searchItems(debouncedQuery).map((result) => ({
      ...result.item, // Spread the actual item data
      type: "item",
      displayType: "Item",
      matches: result.matches || [],
    }));

    // Process pages and guides
    const pageResults = searchPages(debouncedQuery).map((result) => ({
      ...result.item,
      type: "page",
      displayType: "Page",
      url: `/${result.item.slug}`,
      matches: result.matches || [],
    }));

    const guideResults = searchGuides(debouncedQuery).map((result) => ({
      ...result,
      type: "guide",
      displayType: "Guide",
      title: result.title || "Untitled Guide",
      url: result.url || `/wiki/${result.slug || result.id}`,
      matches: result.matches || [],
    }));

    return {
      items: itemResults,
      pages: [...pageResults, ...guideResults],
    };
  }, [debouncedQuery, searchItems, searchPages, searchGuides]);

  // Calculate result counts
  const resultCounts = useMemo(
    () => ({
      all: allResults.items.length + allResults.pages.length,
      items: allResults.items.length,
      pages: allResults.pages.length,
    }),
    [allResults]
  );

  // Filter results based on active tab
  const filteredResults = useMemo(
    () => (activeTab === "items" ? allResults.items : activeTab === "pages" ? allResults.pages : [...allResults.items, ...allResults.pages]),
    [activeTab, allResults]
  );

  // Close dropdown when route changes
  useEffect(() => setIsDropdownOpen(false), [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = async (item) => {
    setIsDropdownOpen(false);
    if (item.type === "item" || item.type === "ugc") {
      try {
        const itemId = item.id;
        const data = await fetchItems({itemId});
        if (data?.[0]) {
          pushModal({
            id: `item-${item.id}`,
            type: "itemModal",
            props: {
              item: data[0],
              onClose: () => {},
              showNavigation: false,
              onNavigate: null,
              totalItems: 1,
              index: null,
            },
          });
        }
      } catch (err) {
        console.error("Failed to fetch item:", err);
      }
    } else {
      navigate(item.url);
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (e.target.value.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const getMatchExcerpt = (item) => {
    if (!item.matches || item.matches.length === 0) {
      return item.meta?.description || "";
    }

    // Find the first content match
    const contentMatch = item.matches.find((match) => match.key.includes("content.data") || match.key.includes("meta.description"));

    if (contentMatch) {
      let text = contentMatch.value;

      // Remove HTML tags (like <br/>)
      text = text.replace(/<[^>]*>?/gm, "");

      // Get the matched portion plus some context
      const [startIdx, endIdx] = contentMatch.indices[0];
      const contextSize = 30; // Characters before/after match
      const start = Math.max(0, startIdx - contextSize);
      const end = Math.min(text.length, endIdx + contextSize);

      let excerpt = text.slice(start, end);

      // Add ellipsis if truncated
      if (start > 0) excerpt = `...${excerpt}`;
      if (end < text.length) excerpt = `${excerpt}...`;

      // Highlight the matched text
      const matchedText = text.slice(startIdx, endIdx + 1);
      excerpt = excerpt.replace(new RegExp(matchedText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "<mark>$&</mark>");

      return excerpt;
    }

    return item.meta?.description || "";
  };

  const isLoading = loadingItems || loadingGuides || loadingPages;

  return (
    <div className="relative w-full max-w-[400px] mx-auto mt-3 text-sm" ref={dropdownRef}>
      <input
        type="text"
        placeholder="Search Wiki..."
        value={query}
        onChange={handleInputChange}
        onFocus={() => query.length > 0 && setIsDropdownOpen(true)}
        className="w-full border px-4 py-2 rounded-lg select-forms"
      />

      {isDropdownOpen && !isLoading && debouncedQuery && (
        <div className="absolute z-50 w-full bg-white shadow-lg rounded-md mt-1 border">
          {/* Simplified tabs */}
          <div className="flex border-b">
            <TabButton active={activeTab === "all"} onClick={() => setActiveTab("all")} count={resultCounts.all}>
              All
            </TabButton>
            <TabButton active={activeTab === "items"} onClick={() => setActiveTab("items")} count={resultCounts.items}>
              Items
            </TabButton>
            <TabButton active={activeTab === "pages"} onClick={() => setActiveTab("pages")} count={resultCounts.pages}>
              Pages
            </TabButton>
          </div>

          {/* Scrollable results area */}
          <div className="max-h-[300px] overflow-y-auto">
            {filteredResults.map((item) => (
              <div key={`${item.type}-${item.id}`} className="p-3 border-b last:border-b-0 hover:bg-gray-100 cursor-pointer" onClick={() => handleOpen(item)}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-200 rounded text-lg mt-1">
                    {item.type === "item" ? <img src={item.image} alt={item.name} className="w-8 h-8 object-contain" /> : item.type === "guide" ? "📚" : "📄"}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{item.name || item.title}</p>
                    {getMatchExcerpt(item) && (
                      <p
                        className="text-sm text-gray-600 mt-1 line-clamp-2" // Limits to 2 lines
                        dangerouslySetInnerHTML={{ __html: getMatchExcerpt(item) }}
                      />
                    )}
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {item.displayType}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, count, children }) => (
  <button
    className={`relative px-4 py-2 text-sm font-medium flex-1 text-center ${
      active ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500 hover:text-gray-700"
    }`}
    onClick={onClick}
  >
    {children}
    {count > 0 && (
      <span className={`absolute top-1 right-2 text-xs rounded-full px-1.5 py-0.5 ${active ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}`}>
        {count}
      </span>
    )}
  </button>
);

export default SearchBar;
